/**
 * Pure H OS · Pre-Production Gap Closure · Uploads.
 *
 * Small file upload widget (base64 → backend `POST /api/pureh/uploads`)
 * plus preview modal for images / PDFs / text. Used from Documents drawer
 * and Files page.
 */
import { useRef, useState } from "react";
import { Card, Button } from "./lib";
import { api } from "../services/api";

const MAX_BYTES = 6 * 1024 * 1024;

export type UploadedRef = { id: string; name: string; mime: string; sizeBytes: number; uploadedAt: string; linkedTo?: string; version?: number; rootId?: string; superseded?: boolean };

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export function UploadWidget({
  linkedTo, onUploaded, testId = "upload-widget", replacesId,
}: {
  linkedTo?: string;
  onUploaded?: (ref: UploadedRef) => void;
  testId?: string;
  replacesId?: string;    // when set, uploads become a new version chained to this id
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<UploadedRef | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  async function onFile(f: File) {
    setErr(null); setOk(null); setBusy(true);
    try {
      if (f.size > MAX_BYTES) throw new Error("File too large (max 6 MB)");
      const b64 = await fileToBase64(f);
      const result = await api.upload(f.name, f.type || "application/octet-stream", b64, linkedTo, replacesId);
      setOk(result as UploadedRef);
      onUploaded?.(result as UploadedRef);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <input
        ref={ref} type="file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button variant="secondary" onClick={() => ref.current?.click()} disabled={busy} testId={`${testId}-button`}>
        {busy ? "Uploading…" : replacesId ? "Upload new version" : "Upload file"}
      </Button>
      {err && <div className="text-xs" style={{ color: "var(--ph-danger)" }} data-testid={`${testId}-error`}>{err}</div>}
      {ok && <div className="text-xs" style={{ color: "var(--ph-muted)" }} data-testid={`${testId}-ok`}>Uploaded · <code>{ok.name}</code> · v{ok.version ?? 1} · {(ok.sizeBytes / 1024).toFixed(1)} kB</div>}
    </div>
  );
}

export function UploadPreview({
  upload, onClose, testId = "upload-preview",
}: {
  upload: UploadedRef | null;
  onClose: () => void;
  testId?: string;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    if (!upload) return;
    setErr(null);
    try {
      const p = await api.uploadPayload(upload.id) as { dataBase64: string; mime: string };
      setDataUrl(`data:${p.mime};base64,${p.dataBase64}`);
      setLoaded(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Preview failed");
    }
  }

  if (!upload) return null;
  if (!loaded && !err) void load();

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid={testId} onClick={onClose}>
      <div className="bg-white rounded max-w-4xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>{upload.mime}</div>
            <div className="text-sm font-semibold" style={{ color: "var(--ph-text)" }}>{upload.name}</div>
          </div>
          <Button variant="ghost" onClick={onClose} testId={`${testId}-close`}>Close</Button>
        </div>
        <div className="p-4">
          {err && <div className="text-sm" style={{ color: "var(--ph-danger)" }}>{err}</div>}
          {!dataUrl && !err && <div className="text-sm" style={{ color: "var(--ph-muted)" }}>Loading…</div>}
          {dataUrl && upload.mime.startsWith("image/") && (
            <img src={dataUrl} alt={upload.name} className="max-w-full max-h-[70vh] mx-auto" data-testid={`${testId}-image`} />
          )}
          {dataUrl && upload.mime === "application/pdf" && (
            <object data={dataUrl} type="application/pdf" className="w-full h-[70vh]" data-testid={`${testId}-pdf`}>
              <div className="text-sm p-4" style={{ color: "var(--ph-muted)" }}>Your browser can&apos;t preview PDFs inline. <a href={dataUrl} download={upload.name}>Download</a></div>
            </object>
          )}
          {dataUrl && (upload.mime.startsWith("text/") || upload.mime === "text/markdown") && (
            <pre className="text-xs whitespace-pre-wrap p-3 rounded" style={{ background: "var(--ph-surface-subtle)", color: "var(--ph-text)" }} data-testid={`${testId}-text`}>
              {(() => { try { return atob(dataUrl.split(",")[1] ?? ""); } catch { return ""; } })()}
            </pre>
          )}
          {dataUrl && !upload.mime.startsWith("image/") && upload.mime !== "application/pdf" && !upload.mime.startsWith("text/") && (
            <div className="text-sm" style={{ color: "var(--ph-muted)" }}>
              Inline preview not supported for this MIME type · <a href={dataUrl} download={upload.name} style={{ color: "var(--ph-accent)" }}>Download</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function UploadsList({ items, onOpen, onDeleted, onReplace, testId = "uploads-list" }: {
  items: UploadedRef[]; onOpen: (u: UploadedRef) => void;
  onDeleted?: (id: string) => void;
  onReplace?: (u: UploadedRef) => void;
  testId?: string;
}) {
  if (items.length === 0) return null;
  // Group by rootId → newest version wins. We surface only the current
  // (non-superseded) version per chain; older versions are one click away
  // via "History" for that row.
  const chains: Record<string, UploadedRef[]> = {};
  for (const u of items) {
    const root = u.rootId ?? u.id;
    (chains[root] ||= []).push(u);
  }
  const current = Object.values(chains).map((arr) => {
    return arr.slice().sort((a, b) => (b.version ?? 1) - (a.version ?? 1))[0];
  });

  async function del(id: string) {
    try { await api.uploadDelete(id); onDeleted?.(id); } catch { /* silent */ }
  }

  return (
    <Card padded={false} testId={testId}>
      <div className="p-3 text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)", borderBottom: "1px solid var(--ph-border-subtle)" }}>
        Uploaded files
      </div>
      {current.map((u) => {
        const chain = chains[u.rootId ?? u.id] ?? [u];
        const versionCount = chain.length;
        return (
          <div key={u.id} data-testid={`${testId}-row-${u.id}`}
               className="w-full text-left p-3 text-sm flex items-center justify-between"
               style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
            <button onClick={() => onOpen(u)} className="flex-1 text-left">
              <div style={{ color: "var(--ph-text)" }}>{u.name} <span className="text-[10px]" style={{ color: "var(--ph-muted)" }}>· v{u.version ?? 1}{versionCount > 1 ? ` of ${versionCount}` : ""}</span></div>
              <div className="text-xs" style={{ color: "var(--ph-muted)" }}>{u.mime} · {(u.sizeBytes / 1024).toFixed(1)} kB · {new Date(u.uploadedAt).toLocaleDateString()}</div>
            </button>
            <div className="flex items-center gap-2 ml-3">
              <button className="text-xs" style={{ color: "var(--ph-accent)" }} onClick={() => onOpen(u)} data-testid={`${testId}-preview-${u.id}`}>Preview</button>
              {onReplace && (
                <button className="text-xs" style={{ color: "var(--ph-accent)" }} onClick={() => onReplace(u)} data-testid={`${testId}-replace-${u.id}`}>Replace</button>
              )}
              <button className="text-xs" style={{ color: "var(--ph-danger)" }} onClick={() => void del(u.id)} data-testid={`${testId}-delete-${u.id}`}>Delete</button>
            </div>
          </div>
        );
      })}
    </Card>
  );
}
